import React, { useEffect, useRef } from "react"
import { Chart } from "chart.js"
import {
  ChoroplethController,
  GeoFeature,
  ColorScale,
  ProjectionScale,
} from "chartjs-chart-geo"
import { feature } from "topojson-client"
import topology from "us-atlas/states-10m.json"
import { GeographicExposureData } from "../../api/quarterly_reports"

// Register Chart.js components
Chart.register(ChoroplethController, GeoFeature, ColorScale, ProjectionScale)

interface USMapProps {
  data?: GeographicExposureData[]
}

// State-to-color mapping based on sub-regions
// sub-regions are a design concern. The actual data fueling the map is region
// based
const STATE_COLORS: Record<string, string> = {
  // Grey - West Coast
  California: "#617491",
  Oregon: "#617491",
  Washington: "#617491",

  // Light Gray - Mountain/Desert West
  Nevada: "#a6b3c4",
  Idaho: "#a6b3c4",
  Montana: "#a6b3c4",
  Wyoming: "#a6b3c4",
  Utah: "#a6b3c4",
  Arizona: "#a6b3c4",
  "New Mexico": "#a6b3c4",
  Colorado: "#a6b3c4",

  // Pale Yellow - Northern Midwest
  "North Dakota": "#ffcc66",
  "South Dakota": "#ffcc66",
  Nebraska: "#ffcc66",
  Kansas: "#ffcc66",
  Minnesota: "#ffcc66",
  Iowa: "#ffcc66",
  Missouri: "#ffcc66",

  // Golden Yellow - Great Lakes
  Wisconsin: "#ffc001",
  Illinois: "#ffc001",
  Indiana: "#ffc001",
  Michigan: "#ffc001",
  Ohio: "#ffc001",

  // Light Blue - South Central (Texas, Oklahoma, Arkansas, Louisiana)
  Texas: "#a8dbff",
  Oklahoma: "#a8dbff",
  Arkansas: "#a8dbff",
  Louisiana: "#a8dbff",

  // Medium Blue - Mississippi, Alabama, Tennessee, Kentucky
  Mississippi: "#61bffd",
  Alabama: "#61bffd",
  Tennessee: "#61bffd",
  Kentucky: "#61bffd",

  // Purple - South Atlantic
  Florida: "#7030a0",
  Georgia: "#7030a0",
  "South Carolina": "#7030a0",
  "North Carolina": "#7030a0",
  Virginia: "#7030a0",
  "West Virginia": "#7030a0",
  Maryland: "#7030a0",
  Delaware: "#7030a0",

  // Light Green - Mid-Atlantic (PA, NJ, NY)
  "New York": "#92d050",
  Pennsylvania: "#92d050",
  "New Jersey": "#92d050",

  // Bright Green - Northeast (everything north/east of PA/NJ/NY)
  Vermont: "#01b050",
  "New Hampshire": "#01b050",
  Connecticut: "#01b050",
  Maine: "#01b050",
  Massachusetts: "#01b050",
  "Rhode Island": "#01b050",
}

const getColorForState = (stateName: string): string => {
  return STATE_COLORS[stateName] || "#CCCCCC"
}

// US sub-regions: no persistent label; revealed via chart tooltip on hover
const US_REGIONS = new Set([
  "West",
  "Midwest",
  "Northeast",
  "South Central",
  "South Atlantic",
])

// Maps each state to its data-level region for tooltip lookup
const STATE_TO_REGION: Record<string, string> = {
  // West
  California: "West",
  Oregon: "West",
  Washington: "West",
  Nevada: "West",
  Idaho: "West",
  Montana: "West",
  Wyoming: "West",
  Utah: "West",
  Arizona: "West",
  "New Mexico": "West",
  Colorado: "West",
  // Midwest
  "North Dakota": "Midwest",
  "South Dakota": "Midwest",
  Nebraska: "Midwest",
  Kansas: "Midwest",
  Minnesota: "Midwest",
  Iowa: "Midwest",
  Missouri: "Midwest",
  Wisconsin: "Midwest",
  Illinois: "Midwest",
  Indiana: "Midwest",
  Michigan: "Midwest",
  Ohio: "Midwest",
  // South Central
  Texas: "South Central",
  Oklahoma: "South Central",
  Arkansas: "South Central",
  Louisiana: "South Central",
  Mississippi: "South Central",
  Alabama: "South Central",
  Tennessee: "South Central",
  Kentucky: "South Central",
  // South Atlantic
  Florida: "South Atlantic",
  Georgia: "South Atlantic",
  "South Carolina": "South Atlantic",
  "North Carolina": "South Atlantic",
  Virginia: "South Atlantic",
  "West Virginia": "South Atlantic",
  Maryland: "South Atlantic",
  Delaware: "South Atlantic",
  // Northeast
  "New York": "Northeast",
  Pennsylvania: "Northeast",
  "New Jersey": "Northeast",
  Vermont: "Northeast",
  "New Hampshire": "Northeast",
  Connecticut: "Northeast",
  Maine: "Northeast",
  Massachusetts: "Northeast",
  "Rhode Island": "Northeast",
}

// Map region names to CSS class names
const getRegionClassName = (regionName: string): string => {
  const classMap: Record<string, string> = {
    West: "region-west",
    Midwest: "region-midwest",
    Northeast: "region-northeast",
    "South Central": "region-south-central",
    "South Atlantic": "region-south-atlantic",
    "Outside of US": "region-non-us",
    Unknown: "region-unknown",
  }
  return classMap[regionName] || ""
}

// Display label for non-US regions
const getRegionLabel = (regionName: string): string => {
  if (regionName === "Outside of US") return "Non-US"
  return regionName
}

interface Annotation {
  name: string
  percentage: number | undefined
  className: string
  isTooltip: boolean
}

const USMap: React.FC<USMapProps> = ({ data }) => {
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstanceRef = useRef<Chart | null>(null)

  const annotations = React.useMemo((): Annotation[] => {
    if (!data) return []

    const nonTotalRegions = data.filter((region) => !region.is_total)

    return nonTotalRegions
      .filter((region) => (region.qpcsp_weighting ?? 0) > 0)
      .map((region) => ({
        name: getRegionLabel(region.geographic_region),
        percentage: region.qpcsp_weighting,
        className: getRegionClassName(region.geographic_region),
        isTooltip: US_REGIONS.has(region.geographic_region),
      }))
  }, [data])

  useEffect(() => {
    if (!chartRef.current) return

    const states = feature(
      topology as any,
      (topology as any).objects.states,
    ) as any

    const filteredStates = states.features.filter(
      (f: any) =>
        f.properties.name !== "Alaska" && f.properties.name !== "Hawaii",
    )

    // Destroy existing chart if it exists
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy()
    }

    // Set higher device pixel ratio for crisp PDF rendering
    const dpr = Math.max(window.devicePixelRatio || 1, 2)
    Chart.defaults.devicePixelRatio = dpr

    // Create new chart
    const ctx = chartRef.current.getContext("2d")
    if (!ctx) return

    chartInstanceRef.current = new Chart(ctx, {
      type: "choropleth",
      data: {
        labels: filteredStates.map((d: any) => d.properties.name),
        datasets: [
          {
            label: "States",
            outline: filteredStates,
            data: filteredStates.map((d: any) => {
              const color = getColorForState(d.properties.name)
              return {
                feature: d,
                value: 1,
                stateColor: color,
              }
            }),
            backgroundColor: (context: any) => {
              if (!context.raw) return "#CCCCCC"
              return context.raw.stateColor || "#CCCCCC"
            },
            borderColor: "#FFFFFF",
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        events: ["mousemove", "mouseout"] as any,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            enabled: true,
            backgroundColor: "#143764",
            titleColor: "#ffffff",
            bodyColor: "#ffffff",
            titleFont: { size: 13, weight: "bold" as const },
            bodyFont: { size: 16, weight: "bold" as const },
            padding: 10,
            cornerRadius: 8,
            displayColors: false,
            callbacks: {
              title: (items: any[]) => {
                if (!items.length) return ""
                const region = STATE_TO_REGION[items[0].label]
                return region || ""
              },
              label: (item: any) => {
                const region = STATE_TO_REGION[item.label]
                if (!region || !data) return ""
                const regionData = data.find(
                  (d) => d.geographic_region === region,
                )
                if (!regionData) return ""
                return `${regionData.qpcsp_weighting.toFixed(1)}%`
              },
            },
          },
        },
        scales: {
          projection: {
            axis: "x",
            projection: "albersUsa",
          },
          color: {
            display: false,
          },
        },
      },
    } as any)

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy()
        chartInstanceRef.current = null
      }
    }
  }, [data])

  return (
    <div className="us-map-container">
      <canvas ref={chartRef} />
      {annotations
        .filter((annotation) => !annotation.isTooltip)
        .map(
          (annotation, index) =>
            annotation.percentage !== undefined && (
              <div
                key={index}
                className={`region-annotation ${annotation.className}`}
              >
                <div className="region-name">{annotation.name}</div>
                <div className="region-percentage">
                  {annotation.percentage.toFixed(1)}%
                </div>
              </div>
            ),
        )}
    </div>
  )
}

export default USMap
